<template>
	<div :id="pageID">
		<Navigation />
		<DynamicComponent
			v-for="(section, index) in sections"
			:id="`${section.id}-${index}`"
			:key="`${section.id}-${index}`"
			:component-id="section.vueComponentID"
			:data="section"
		/>
		<Footer />
	</div>
</template>

<script setup>
import { useRoute } from 'vue-router';

const { locale, locales } = useI18n();
const route = useRoute();
const config = useRuntimeConfig();
const slug = computed(() => route.params.slug || 'home');
const pageID = ref('');

const toPascalCase = (string) => {
	if(!string) {
		return '';
	}

	return string.replace(/(^\w|[-_ ]+\w)/g, match => match.replace(/[-_ ]+/, '').toUpperCase());
}

// Fetch landing page data
const { data: pageData, pending, error, refresh } = await useFetch('/api/contentful/page', {
	query: {
		locale: `${locale.value}-CA`,
		slug: route.params.slug || 'home'
	},
	key: `page-${slug.value}-${locale.value}`,
	watch: [slug]
});

const page = computed(() => {
	if (!pageData.value) {
		console.log('No page data');
		return null;
	}

	if (!pageData.value.fields) {
		console.log('No fields in page data');
		return null;
	}

	return pageData.value.fields;
});

const localizedPaths = computed(() => {
	if (!pageData.value) {
		console.log("PAGES/[SLUG].VUE: pageData is null, returning empty.");
		return {};
	}

	const paths = {};
	const slugField = pageData.value.slugs;


	pageID.value = slugField['en-CA'];

	if (!slugField || typeof slugField === 'string' || Object.keys(slugField).length === 0) {
		console.warn("PAGES/[SLUG].VUE: slugField is not a proper localized object or is empty.", slugField);
		return {};
	}

	locales.value.forEach(lang => {
		const i18nLocaleCode = lang.code; // 'en', 'fr'
		const contentfulLocaleIso = lang.iso || lang.code; // 'en-CA', 'fr-CA' (from nuxt.config.ts)

		// Check if the Contentful slug field has a value for this locale key
		const localizedSlug = slugField[contentfulLocaleIso]; // Direct access

		if (localizedSlug) {
			paths[i18nLocaleCode] = `/${localizedSlug}`;
		} else {
			console.warn(`PAGES/[SLUG].VUE: No localized slug found in Contentful for key '${contentfulLocaleIso}' (i18n code '${i18nLocaleCode}').`);
		}
	});

	return paths;
});

watch(localizedPaths, (newPaths) => {
	if (Object.keys(newPaths).length > 0) {
		route.meta.localizedPaths = newPaths;
	} else {
		console.warn("PAGES/[SLUG].VUE: Watcher received empty paths. Not updating route.meta.");
	}
}, { immediate: true });

definePageMeta({
	validate: async (route) => {
		const slug = route.params.slug || 'home';
		const locale = route.path.startsWith('/en/') ? 'en' : 'fr';
		const res = await $fetch('/api/contentful/page', {
			query: {
				slug,
				locale: `${locale}-CA`,
				exists: true
			}
		});

		if(res && res?.exists === false) {
			return false;
		}

		return true;
	}
});


const sections = computed(() => {
	if (!page.value) {
		return [];
	}

	if (Array.isArray(page.value.components)) {
		return page.value.components.map(item => ({
			id: item?.sys?.id,
			vueComponentID: toPascalCase(item?.sys?.contentType?.sys.id),
			data: item?.fields
		}));
	}

	return [];
});

const noIndexNoFollow = page?.value?.noIndexNoFollow || config.public.noIndexNoFollow ? {
	name: 'robots',
	content: 'noindex, nofollow'
} : {};

const canonicalUrl = computed(() => {
	if (!page.value) return ''
	const baseUrl = config.public.baseUrl
	const slugFr = localizedPaths.value?.fr === '/home' ? '' : localizedPaths.value?.fr
	const slugEn = localizedPaths.value?.en === '/home' ? '' : localizedPaths.value?.en
	return locale.value === 'en' ? `${baseUrl}/en${slugEn}` : `${baseUrl}${slugFr}`
});

// hreflang links
const hreflangLinks = computed(() => {
	if (!page.value) return []

	const baseUrl = config.public.baseUrl
	const slugFr = localizedPaths.value?.fr === '/home' ? '' : localizedPaths.value?.fr
	const slugEn = localizedPaths.value?.en === '/home' ? '' : localizedPaths.value?.en

	return [
		{ rel: 'canonical', href: canonicalUrl.value },
		{ rel: 'alternate', hreflang: 'fr-CA', href: `${baseUrl}${slugFr}` },
		{ rel: 'alternate', hreflang: 'en-CA', href: `${baseUrl}/en${slugEn}` },
		{ rel: 'alternate', hreflang: 'x-default', href: `${baseUrl}${slugFr}` }
	]
});

watchEffect(() => {
	const alternateLinks = computed(() => {
		if (!page.value) {
			return [];
		}

		const baseUrl = config.public.baseUrl;

		return locales.value.map(loc => {
			// Get the slug for each locale from Contentful
			const localizedSlug = localizedPaths.value[loc.code] !== '/home' ? localizedPaths.value[loc.code] : '';
			let code = loc.code;

			if(localizedPaths.value[loc.code].includes('home') && loc.code === 'fr') {
				code = '';
			}

			return {
				rel: 'x-default',
				hreflang: loc.code,
				href: `${baseUrl}/${code}${localizedSlug}`
			};
		});
	});

	useHead({
		link: hreflangLinks,
		meta: [
			{ 'http-equiv': 'Content-Type', content: 'text/html; charset=utf-8' },
			noIndexNoFollow
		]
	});
});

useHead({
	script: [
		{
			type: 'application/ld+json',
			key: 'schema-org',
			innerHTML: JSON.stringify({
				"@context": "https://schema.org",
				"@graph": [
					{
						"@type": "Organization",
						"@id": `${$t('schema.url')}/${route.params.slug === 'home' ? '' : route.params.slug}`,
						"name": "Immersive Moto",
						"url": `${$t('schema.url')}/${route.params.slug === 'home' ? '' : route.params.slug}`,
						"email": "contact@immersivemoto.ca",
						"founder": "Nicolas Poliquin",
						"description": page?.value?.metaDescription,
						"logo": "http://images.ctfassets.net/aobuf66vdsqn/2G8z2HKSBW0FipMTKM93kF/1b52020e2982fd67d0a7f71c8d85c267/immersive-moto-yellow-no-tagline.svg",
					},
					page?.value?.schema || {}
				]
			})
		}
	]
});

useSeoMeta({
	title: `${page?.value?.metaTitle} - Immersive Moto`,
	description: page?.value?.metaDescription,
	ogTitle:`${page?.value?.metaTitle} - Immersive Moto`,
	ogDescription: page?.value?.metaDescription,
	ogImage: `https:${page?.value?.metaImage.fields.file.url}`,
	ogSiteName: `${page?.value?.metaTitle} - Immersive Moto`,
	ogType: 'website',
	ogUrl: `${useRequestURL().origin}${route.fullPath}`
});

onMounted(() => {
	if(process.client && window.outerWidth <= 1023) {
		if(document.body.classList.contains('mobile-nav-is-open')) {
			document.body.classList.toggle('mobile-nav-is-open');
		}
	}
});

console.log(process.env.NODE_ENV)
</script>